import { useEffect, useState } from 'react'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import type { FootballMatch } from '@/features/poules/api'
import { canPredictMatch } from '@/features/poules/components/PouleMatchesTab'
import { formatKickoffDateTime } from '@/lib/kickoff'
import { useTranslation } from '@/i18n'

const QUICK_SCORE_COLUMNS: Array<Array<[number, number]>> = [
  [
    [1, 0],
    [2, 0],
    [2, 1],
    [3, 0],
  ],
  [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  [
    [0, 1],
    [0, 2],
    [1, 2],
    [1, 3],
  ],
]

export function PoulePredictionModal({
  match,
  isSaving,
  errorMessage,
  onClose,
  onSave,
}: {
  match: FootballMatch
  isSaving: boolean
  errorMessage: string | undefined
  onClose: () => void
  onSave: (homePred: number, awayPred: number) => void
}) {
  const { t } = useTranslation()
  const [homePred, setHomePred] = useState<number | ''>(match.user_prediction?.home_pred ?? '')
  const [awayPred, setAwayPred] = useState<number | ''>(match.user_prediction?.away_pred ?? '')

  useEffect(() => {
    setHomePred(match.user_prediction?.home_pred ?? '')
    setAwayPred(match.user_prediction?.away_pred ?? '')
  }, [match])

  const canPredict = canPredictMatch(match)
  const canSave = homePred !== '' && awayPred !== '' && canPredict && !isSaving

  return (
    <div className="animate-drawer-backdrop-in fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm">
      <div className="flex h-full items-start justify-center overflow-y-auto p-4 pt-5 lg:items-center">
        <div
          className="animate-modal-panel-in relative w-full max-w-lg rounded-[1rem] border border-white/[0.07] bg-[#0d1113] p-4 shadow-[0_18px_56px_rgba(0,0,0,0.5)]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="poule-prediction-title"
        >
          <ModalCloseButton
            onClick={onClose}
            disabled={isSaving}
            label={t('Sluit voorspellen')}
            className="absolute right-3 top-3 h-8 w-8"
          />
          <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-surface-700" />
          <div className="px-8 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
              <span id="poule-prediction-title">{t('Wedstrijd voorspellen')}</span>
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <TeamBlock team={match.home_team} form={match.home_form} t={t} />
              <div className="w-full">
                <p className="text-xs font-semibold text-surface-400">
                  {formatKickoffDateTime(match.kickoff_utc, match.kickoff_nl)}
                </p>
                {match.stage_name ? (
                  <span className="mt-2 inline-flex rounded-full bg-surface-900 px-2.5 py-1 text-xs font-semibold text-surface-400">
                    {match.stage_name}
                  </span>
                ) : null}
              </div>
              <TeamBlock team={match.away_team} form={match.away_form} t={t} />
            </div>
          </div>

          <div className="mt-4 rounded-[1rem] border border-[#202427] bg-[#0a0d0f] p-3.5">
            <div className="grid grid-cols-3 gap-2">
              {QUICK_SCORE_COLUMNS.map((column, columnIndex) => (
                <div key={columnIndex} className="flex min-h-[11rem] flex-col gap-2">
                  {column.map(([home, away]) => {
                    const selected = homePred === home && awayPred === away
                    return (
                      <button
                        key={`${home}-${away}`}
                        type="button"
                        onClick={() => onSave(home, away)}
                        disabled={!canPredict || isSaving}
                        className={[
                          'rounded-[0.8rem] border px-2.5 py-2 text-sm font-black transition',
                          selected
                            ? 'border-accent-500/70 bg-[#182326] text-white shadow-[0_10px_28px_rgba(0,126,181,0.16)]'
                            : 'border-white/[0.08] bg-[#111416] text-surface-300 hover:border-white/[0.18] hover:bg-[#151a1c] focus-visible:border-accent-400 focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f]',
                          'disabled:cursor-not-allowed disabled:opacity-45',
                        ].join(' ')}
                      >
                        {home}-{away}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>

            <p className="mt-4 text-center text-xs font-semibold text-surface-300">
              {t('Vul de uitslag in')}
            </p>

            <div className="mt-3 flex items-center justify-center gap-2.5">
              <ScoreInput
                value={homePred}
                onChange={setHomePred}
                disabled={!canPredict || isSaving}
                label={t('Uitslag {team}', { team: match.home_team.name })}
              />
              <span className="text-base font-black text-surface-400">-</span>
              <ScoreInput
                value={awayPred}
                onChange={setAwayPred}
                disabled={!canPredict || isSaving}
                label={t('Uitslag {team}', { team: match.away_team.name })}
              />
            </div>
          </div>

          {!canPredict ? (
            <p className="mt-3 rounded-lg bg-amber-950/40 px-3.5 py-2.5 text-center text-[11px] text-amber-100">
              {match.prediction_locked_reason || t('Voorspellen is voor deze wedstrijd nu niet beschikbaar.')}
            </p>
          ) : null}

          {errorMessage ? (
            <p className="mt-3 rounded-lg bg-red-950/50 px-3.5 py-2.5 text-center text-[11px] text-red-200">
              {errorMessage}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => {
              if (homePred === '' || awayPred === '') return
              onSave(homePred, awayPred)
            }}
            disabled={!canSave}
            className="mt-3.5 flex w-full items-center justify-center rounded-[0.9rem] bg-accent-600 px-4 py-2.5 text-sm font-black text-white transition hover:bg-accent-500 focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113] disabled:cursor-not-allowed disabled:opacity-45"
          >
            {isSaving ? t('Opslaan…') : t('Voorspelling opslaan')}
          </button>
        </div>
      </div>
    </div>
  )
}

function TeamBlock({
  team,
  form,
  t,
}: {
  team: FootballMatch['home_team']
  form?: string[] | undefined
  t: Translate
}) {
  return (
    <div className="flex w-full flex-col items-center gap-1.5">
      {team.logo_url ? (
        <img src={team.logo_url} alt="" className="h-10 w-12 object-contain" />
      ) : (
        <span className="flex h-10 w-12 items-center justify-center rounded-sm bg-surface-800 text-sm font-black uppercase text-surface-300">
          {getInitials(team.name)}
        </span>
      )}
      <p className="text-sm font-medium text-surface-200">{team.name}</p>
      <TeamForm form={form} t={t} />
    </div>
  )
}

function TeamForm({ form, t }: { form?: string[] | undefined; t: Translate }) {
  if (!form || form.length === 0) return null

  return (
    <div
      aria-label={form.slice(0, 5).map((item) => getFormLabel(item, t)).join(', ')}
      className="flex items-center gap-1"
    >
      {form.slice(0, 5).map((item, index) => (
        <span
          key={`${item}-${index}`}
          aria-hidden="true"
          className={`h-2 w-2 rounded-full ${getFormDotClass(item)}`}
          title={getFormLabel(item, t)}
        />
      ))}
    </div>
  )
}

function ScoreInput({
  value,
  onChange,
  disabled,
  label,
}: {
  value: number | ''
  onChange: (value: number | '') => void
  disabled: boolean
  label: string
}) {
  return (
    <input
      type="number"
      min="0"
      max="99"
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        const raw = event.target.value
        onChange(raw === '' ? '' : Number(raw))
      }}
      className="score-input-no-spinner h-10 w-12 rounded-[0.8rem] border border-white/[0.08] bg-[#111416] px-2 text-center text-lg font-black text-white outline-none transition hover:border-white/[0.18] focus:border-accent-400 focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f] disabled:opacity-50"
    />
  )
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function getFormDotClass(value: string) {
  const normalized = value.trim().toLowerCase()
  if (normalized.startsWith('w')) return 'bg-emerald-400'
  if (normalized.startsWith('d')) return 'bg-amber-300'
  if (normalized.startsWith('l')) return 'bg-red-400'
  return 'bg-surface-600'
}

function getFormLabel(value: string, t: Translate) {
  const normalized = value.trim().toLowerCase()
  if (normalized.startsWith('w')) return t('Winst')
  if (normalized.startsWith('d')) return t('Gelijkspel')
  if (normalized.startsWith('l')) return t('Verlies')
  return t('Onbekende vorm')
}

type Translate = (key: string, values?: Record<string, string | number>) => string
